import { useEffect, useState } from 'react';
import { Download, ExternalLink, Printer } from 'lucide-react';

import { Button } from '@/components/Button/Button';
import { DocumentPreview } from '@/components/DocumentPreview/DocumentPreview';
import { Spinner } from '@/components/Spinner/Spinner';
import { TextLink } from '@/components/TextLink/TextLink';
import {
    downloadFile,
    fetchFile,
    printBlob,
    printFile,
    saveBlob,
    unprintableCharacters,
} from '@/lib/download';
import { cn } from '@/util/utils';

import { SHIPPING } from './copy';

const D = SHIPPING.documents;

/** A PDF as the viewer shows it: its title and subtitle, where it is, and what Download saves it as. */
export interface PdfDocument {
    title: string;
    subtitle: string;
    url: string;
    /** The card's name for it, not the store's key. */
    fileName: string;
}

/**
 * A DHL document in the document viewer, with Download and Print: what the Shipping & returns tab
 * opens a label, a waybill copy or an invoice in, and the orders table's Documents fact too
 * (ALS-262), so a document opens the same way wherever it is named.
 */
export function PdfViewer({
    document,
    onClose,
}: {
    document: PdfDocument | null;
    onClose: () => void;
}) {
    // Here rather than in the sheet, so Download and Print hand over the copy on screen.
    const file = usePdfFile(document?.url ?? null);

    return (
        <DocumentPreview
            open={document !== null}
            onOpenChange={(next) => (next ? undefined : onClose())}
            title={document?.title ?? ''}
            subtitle={document?.subtitle}
            actions={
                document ? (
                    <>
                        {/*
                          The copy on screen, saved under the card's name. Not the link: on staging
                          and production it is a signed one that expires five minutes after the
                          viewer opened (ALS-493), and a label stays open while a parcel is packed.
                          Only a PDF that never arrived is fetched again.
                        */}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                                file.state === 'ready'
                                    ? saveBlob(file.blob, document.fileName)
                                    : void downloadFile(
                                          document.url,
                                          document.fileName,
                                          D.downloadFailed,
                                      )
                            }
                        >
                            <Download />
                            {D.download}
                        </Button>
                        {/* Straight to the print dialog, as the prototype's Print does, from the same copy. */}
                        <Button
                            size="sm"
                            onClick={() =>
                                file.state === 'ready'
                                    ? printBlob(file.blob, D.printFailed)
                                    : void printFile(document.url, D.printFailed)
                            }
                        >
                            <Printer />
                            {D.print}
                        </Button>
                    </>
                ) : null
            }
        >
            {document ? <PdfSheet document={document} file={file} /> : null}
        </DocumentPreview>
    );
}

/**
 * The viewer's PDF: still being fetched, ready as bytes and a URL of this page's own, or refused;
 * refused for good, naming the characters, when its fonts cannot draw some of its text (ALS-564).
 */
type PdfFile =
    | { state: 'loading' }
    | { state: 'ready'; blob: Blob; src: string }
    | { state: 'failed'; unprintable: string | null };

/**
 * The PDF fetched into the page and handed back as a blob URL (ALS-404). On staging and production
 * CloudFront sends every file, the API's and the asset CDN's alike, with `X-Frame-Options: DENY`
 * and `frame-ancestors 'none'`, which forbid drawing it inside any page, the admin's own included.
 * Those headers belong to that response and not to a copy of its bytes, so the copy draws where
 * the file itself would not. The fetch is dropped and the URL let go when the viewer closes or
 * moves to another document, which starts from its own fetch.
 */
function usePdfFile(url: string | null): PdfFile {
    // Remembered with the address it is for, so another document reads as loading until its own
    // fetch answers, with no reset to make on the way.
    const [loaded, setLoaded] = useState<{ url: string; file: PdfFile } | null>(null);

    useEffect(() => {
        if (url === null) return;
        const controller = new AbortController();
        let src: string | null = null;
        fetchFile(url, controller.signal).then(
            (fetched) => {
                if (controller.signal.aborted) return;
                // Typed as a PDF whatever the store kept it as, so the browser's viewer takes it.
                const blob = new Blob([fetched], { type: 'application/pdf' });
                src = URL.createObjectURL(blob);
                setLoaded({ url, file: { state: 'ready', blob, src } });
            },
            (error: unknown) => {
                if (controller.signal.aborted) return;
                setLoaded({
                    url,
                    file: { state: 'failed', unprintable: unprintableCharacters(error) },
                });
            },
        );
        return () => {
            controller.abort();
            if (src !== null) URL.revokeObjectURL(src);
        };
    }, [url]);

    return loaded !== null && loaded.url === url ? loaded.file : { state: 'loading' };
}

/** The white sheet on the viewer's muted ground: a spinner, the PDF, or why it is not there. */
function PdfSheet({ document, file }: { document: PdfDocument; file: PdfFile }) {
    const sheet = 'h-[70dvh] w-full rounded-sm bg-white';

    if (file.state === 'ready') {
        return (
            // An object rather than an iframe: the browser's PDF viewer will not draw inside a
            // sandboxed frame, and a browser with no viewer gets the link to the file instead.
            <object
                data={file.src}
                type="application/pdf"
                aria-label={document.title}
                className={cn(sheet, 'border-0')}
            >
                <a href={document.url} target="_blank" rel="noopener noreferrer">
                    {D.open}
                </a>
            </object>
        );
    }

    return (
        <div className={cn(sheet, 'flex flex-col items-center justify-center gap-3 p-6')}>
            {file.state === 'loading' ? (
                <Spinner aria-label={D.loading} className="text-primary size-6" />
            ) : file.unprintable !== null ? (
                // A tab would be refused the same way, so it is not offered.
                <p role="alert" className="max-w-md text-center text-sm">
                    {D.unprintable(file.unprintable)}
                </p>
            ) : (
                <>
                    <p role="alert" className="text-sm">
                        {D.failed}
                    </p>
                    {/* A tab of its own loads the file whole, which no frame header forbids. */}
                    <TextLink href={document.url} target="_blank" rel="noopener noreferrer">
                        {D.open}
                        <ExternalLink aria-hidden />
                    </TextLink>
                </>
            )}
        </div>
    );
}
